import { useCallback, useRef, useState } from 'react';

const INITIAL = 50;
const MIN = 0;
const MAX = 100;

const clamp = (n) => Math.max(MIN, Math.min(MAX, n));

/**
 * Estado de temperatura del termómetro arcade.
 *
 * Sustituye el `progress` monotónico del backend por un valor 0..100 que sube y
 * baja según las señales de cada turno: mastery_signals, stance, hint y delta
 * de progress backend. Todo se infiere en frontend a partir de los eventos SSE
 * (observer / done) que ya emite el simulador.
 *
 * Regla resumida (clamp 0..100):
 *  - mastery_signals con label "demonstrated"/"strong"/"clear"     → +12
 *  - mastery_signals con label "missed"/"weak"/"absent"            → −12
 *  - stance hostile/closed/resistant + sin progreso este turno     → −5
 *  - stance supportive/open + progreso este turno                  → +4
 *  - hint nuevo (sin progreso) — coach corrigiendo                 → −3
 *  - progress backend sube vs turno anterior                       → +5
 *  - progress backend cae (raro, solo si llega)                    → −5
 */
export function useTemperature(initial = INITIAL) {
    const [temperature, setTemperature] = useState(initial);
    const lastBackendProgressRef = useRef(0);

    const reset = useCallback(() => {
        setTemperature(initial);
        lastBackendProgressRef.current = 0;
    }, [initial]);

    /**
     * Procesa una señal de turno y devuelve el nuevo valor.
     * @param {Object} signal
     * @param {Array<{label?: string, score?: number}>} [signal.masterySignals]
     * @param {string} [signal.stance]
     * @param {string} [signal.hint]
     * @param {boolean} [signal.hintIsNew]
     * @param {number}  [signal.progress] valor 0..1 del backend
     */
    const applySignal = useCallback((signal = {}) => {
        let delta = 0;

        const ms = Array.isArray(signal.masterySignals) ? signal.masterySignals : [];
        for (const s of ms) {
            const label = String(s?.label || s?.status || '').toLowerCase();
            const score = typeof s?.score === 'number' ? s.score : null;
            if (
                label.includes('demonstrated') ||
                label.includes('strong') ||
                label.includes('clear') ||
                label.includes('met') ||
                (score !== null && score >= 0.7)
            ) {
                delta += 12;
            } else if (
                label.includes('missed') ||
                label.includes('weak') ||
                label.includes('absent') ||
                label.includes('failed') ||
                (score !== null && score <= 0.3)
            ) {
                delta -= 12;
            }
        }

        const prevProgress = lastBackendProgressRef.current;
        const curProgress = typeof signal.progress === 'number' ? signal.progress : prevProgress;
        const progressDelta = curProgress - prevProgress;
        if (progressDelta > 0.02) delta += 5;
        else if (progressDelta < -0.02) delta -= 5;
        lastBackendProgressRef.current = curProgress;

        const stance = String(signal.stance || '').toLowerCase();
        if (['hostile', 'closed', 'resistant', 'firm', 'skeptical'].includes(stance)) {
            if (progressDelta <= 0) delta -= 5;
        } else if (['supportive', 'open', 'conciliatory'].includes(stance)) {
            if (progressDelta > 0) delta += 4;
        }

        if (signal.hintIsNew && progressDelta <= 0) {
            delta -= 3;
        }

        if (delta === 0) return;

        setTemperature((t) => clamp(t + delta));
    }, []);

    return { temperature, setTemperature, applySignal, reset };
}

export default useTemperature;
